import { useEffect, useRef, useState, type ReactNode } from 'react';
import { animate, m, useReducedMotion } from 'motion/react';
import { AlertTriangle, Info, Lock, CheckCircle2, XCircle } from 'lucide-react';
import { cx } from '@/lib/cx';
import { big, type Decimal } from '@/lib/format/decimal';
import { formatMoney, type Currency } from '@/lib/format/money';
import { Tooltip } from './Controls';

export function Card({
  className,
  children,
  as: As = 'section',
  interactive,
}: {
  className?: string;
  children: ReactNode;
  as?: 'section' | 'div' | 'article';
  interactive?: boolean;
}) {
  return (
    <As
      className={cx(
        'card',
        interactive && 'transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-px hover:border-gold-200 hover:shadow-e2',
        className,
      )}
    >
      {children}
    </As>
  );
}

export function SectionTitle({ children, aside, overline, className }: { children: ReactNode; aside?: ReactNode; overline?: ReactNode; className?: string }) {
  return (
    <div className={cx('mb-4 flex items-end justify-between gap-3', className)}>
      <div>
        {overline && <div className="overline mb-1">{overline}</div>}
        <h2 className="font-display text-[21px] leading-tight text-ink-900">{children}</h2>
      </div>
      {aside}
    </div>
  );
}

export type Tone = 'neutral' | 'gold' | 'jade' | 'terra' | 'danger' | 'slate' | 'muted';

const tones: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-700 border-line-strong',
  gold: 'bg-gold-50 text-gold-700 border-gold-200',
  jade: 'bg-jade-50 text-jade-600 border-jade-600/20',
  terra: 'bg-terra-50 text-terra-600 border-terra-600/20',
  danger: 'bg-danger-50 text-danger-600 border-danger-600/20',
  slate: 'bg-slate-50 text-slate-600 border-slate-600/20',
  muted: 'bg-surface-1 text-ink-400 border-line',
};
const dots: Record<Tone, string> = {
  neutral: 'bg-ink-400',
  gold: 'bg-gold-500',
  jade: 'bg-jade-600',
  terra: 'bg-terra-600',
  danger: 'bg-danger-600',
  slate: 'bg-slate-600',
  muted: 'bg-ink-300',
};

export function Chip({
  tone = 'neutral',
  children,
  pulse,
  className,
  icon,
}: {
  tone?: Tone;
  children: ReactNode;
  pulse?: boolean;
  className?: string;
  icon?: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-medium whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {icon ?? <span className={cx('size-1.5 rounded-full', dots[tone], pulse && 'animate-pulse-soft')} />}
      {children}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton', className)} aria-hidden />;
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <m.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cx('marble flex flex-col items-center rounded-(--radius-card) border border-dashed border-line-strong px-6 py-12 text-center', className)}
    >
      {icon && (
        <div className="mb-4 flex size-14 items-center justify-center rounded-2xl border border-gold-200 bg-surface-0 text-gold-600 shadow-e2">
          {icon}
        </div>
      )}
      <h3 className="font-display text-[22px] text-ink-900">{title}</h3>
      {children && <p className="mt-1.5 max-w-md text-[13.5px] text-ink-500">{children}</p>}
      {action && <div className="mt-5">{action}</div>}
    </m.div>
  );
}

const bannerTones = {
  info: { cls: 'border-slate-600/20 bg-slate-50 text-slate-600', Icon: Info },
  warning: { cls: 'border-terra-600/25 bg-terra-50 text-terra-600', Icon: AlertTriangle },
  danger: { cls: 'border-danger-600/25 bg-danger-50 text-danger-600', Icon: XCircle },
  success: { cls: 'border-jade-600/25 bg-jade-50 text-jade-600', Icon: CheckCircle2 },
  gold: { cls: 'border-gold-200 bg-gold-50 text-gold-700', Icon: Info },
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: keyof typeof bannerTones;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { cls, Icon } = bannerTones[tone];
  return (
    <m.div
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('flex items-start gap-3 rounded-xl border px-4 py-3 text-[13.5px]', cls, className)}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 text-ink-700">
        {title && <div className="font-semibold text-ink-900">{title}</div>}
        {children}
      </div>
      {action}
    </m.div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-line-strong bg-surface-0 px-1 font-sans text-[11px] text-ink-500 shadow-e1">
      {children}
    </kbd>
  );
}

/** A value hidden by permissions arrives as null: it is shown as "—" with a lock, never as zero. */
export function HiddenValue({ label = 'Sin permiso para ver este dato' }: { label?: string }) {
  return (
    <Tooltip content={label}>
      <span className="inline-flex items-center gap-1 text-ink-300" aria-label={label}>
        <Lock className="size-3" aria-hidden />—
      </span>
    </Tooltip>
  );
}

export function Money({
  value,
  currency,
  cents,
  className,
}: {
  value: Decimal | null | undefined;
  currency: Currency;
  cents?: boolean;
  className?: string;
}) {
  if (value === null || value === undefined) return <HiddenValue />;
  return <span className={cx('tabular', className)}>{formatMoney(value, currency, { cents })}</span>;
}

/**
 * Animates a server total when it changes: the digits roll to the new value (300 ms). The final text is always the
 * exact formatted string, so there is no floating-point rounding at rest.
 */
export function NumberTicker({ value, format, className }: { value: Decimal; format: (v: Decimal) => string; className?: string }) {
  const reduce = useReducedMotion();
  const [text, setText] = useState(() => format(value));
  const previous = useRef(value);
  const [flash, setFlash] = useState(0);

  useEffect(() => {
    const from = previous.current;
    previous.current = value;
    if (from === value) {
      setText(format(value));
      return;
    }
    setFlash((f) => f + 1);
    if (reduce) {
      setText(format(value));
      return;
    }
    const a = big(from);
    const b = big(value);
    const controls = animate(0, 1, {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (t) => setText(format(a.plus(b.minus(a).times(t)).round(0).toString())),
      onComplete: () => setText(format(value)),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <span className={cx('tabular relative inline-block', className)}>
      {text}
      {flash > 0 && (
        <m.span
          key={flash}
          aria-hidden
          className="pointer-events-none absolute -inset-x-1 inset-y-0 rounded bg-gold-200/50"
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        />
      )}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx('inline-block size-4 animate-spin rounded-full border-2 border-gold-200 border-t-gold-600', className)}
      aria-label="Cargando"
    />
  );
}
